// src/components/VendorDetails.js

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckIcon,
  StarIcon as StarOutline,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, PencilIcon } from '@heroicons/react/24/solid';




export default function VendorDetails() {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  
const [showContactModal, setShowContactModal] = useState(false);
const [contactName, setContactName] = useState('');
const [contactEmail, setContactEmail] = useState('');
const [contactConsent, setContactConsent] = useState(false);
const [contactSubmitting, setContactSubmitting] = useState(false);
const [contactSuccess, setContactSuccess] = useState(false);
const [contactError, setContactError] = useState('');


function maskPhone(phone) {
  if (!phone) return '';
  // Remove spaces and dashes
  phone = phone.replace(/[\s-]/g, '');

  // Always handle with country code
  let match = phone.match(/^(\+?\d{1,3})(\d{2,})(\d{2})$/);
  if (!match) {
    // Fallback: mask all but last 2 digits for any number
    return phone.replace(/.(?=..)/g, '*');
  }
  const [, code, mid, last2] = match;
  const stars = '*'.repeat(mid.length - 2);
  return `${code}${stars}${mid.slice(-2)}${last2}`;
}
  // All reviews & derived
  const [reviews, setReviews] = useState([]);         // full list
  const [topReviews, setTopReviews] = useState([]);   // will hold latest three
  const [avgRating, setAvgRating] = useState(0);

  const [loading, setLoading] = useState(true);

  // About toggle
  const [showFullAbout, setShowFullAbout] = useState(false);

  // OTP review flow
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [step, setStep] = useState('input'); // 'input' or 'otp'
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    reviewer_name: '',
    reviewer_email: '',
    review_text: '',
  });
  const [reviewError, setReviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [otp, setOtp] = useState('');

  // "All reviews" modal
  const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);

  // Fetch vendor
  const fetchVendor = async () => {
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .eq('id', id)
      .single();
    if (!error) setVendor(data);
  };

  // Fetch reviews in descending order (newest first)
  const fetchReviews = async () => {
    const { data, error } = await supabase
      .from('reviews')
      .select('id, rating, reviewer_name, review_text, updated_at')
      .eq('vendor_id', id)
      .eq('is_verified', true)
      .order('updated_at', { ascending: false });
    const all = data || [];
    setReviews(all);
    setTopReviews(all.slice(0, 3));
    if (all.length) {
      const avg = all.reduce((sum, r) => sum + r.rating, 0) / all.length;
      setAvgRating(Math.round(avg * 2) / 2);
    } else {
      setAvgRating(0);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchVendor(), fetchReviews()]).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-center py-12">Loading vendor...</p>;
  if (!vendor) return <p className="text-center py-12">Vendor not found.</p>;

  // Derived display values
  const imgSrc = vendor.display_image_url?.trim() || '/assets/default-profile.png';
  const services = vendor.service_description
    ? vendor.service_description.split(/[,;.]+/).map(s => s.trim()).filter(Boolean)
    : [];
  const avail = vendor.availability_notes;

  // Open review dialog
  const openReview = () => {
    setReviewForm({ rating: 5, reviewer_name: '', reviewer_email: '', review_text: '' });
    setReviewError('');
    setOtp('');
    setStep('input');
    setShowReviewModal(true);
  };

  const handleReviewChange = e => {
    const { name, value } = e.target;
    setReviewForm(f => ({ ...f, [name]: value }));
  };

  // 1️⃣ Insert-or-update review + trigger OTP
  const handleSubmitReview = async e => {
    e.preventDefault();
    setReviewError('');
    const { reviewer_name, reviewer_email, review_text, rating } = reviewForm;
    if (!reviewer_name || !reviewer_email || !review_text) {
      setReviewError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);

    // check existing
    const { data: existing, error: fetchErr } = await supabase
      .from('reviews')
      .select('id')
      .eq('vendor_id', id)
      .eq('reviewer_email', reviewer_email)
      .maybeSingle();

    if (fetchErr) {
      setSubmitting(false);
      setReviewError('Error checking existing review.');
      return;
    }

    let rid;
    if (existing) {
      // update
      const { error: updErr } = await supabase
        .from('reviews')
        .update({
          reviewer_name,
          review_text,
          rating,
          verify_code: null,
          verify_sent_at: null,
          is_verified: false,
        })
        .eq('id', existing.id);
      if (updErr) {
        setSubmitting(false);
        setReviewError('Failed to update review.');
        return;
      }
      rid = existing.id;
    } else {
      // insert
      const { data: inserted, error: insErr } = await supabase
        .from('reviews')
        .insert([{ vendor_id: id, reviewer_name, reviewer_email, review_text, rating }])
        .select('id')
        .single();
      if (insErr || !inserted) {
        setSubmitting(false);
        setReviewError('Failed to submit review.');
        return;
      }
      rid = inserted.id;
    }

    setReviewId(rid);

    // send OTP
    const { error: fnError } = await supabase.functions.invoke('sendReviewOtp', {
      body: JSON.stringify({ review_id: rid, email: reviewer_email }),
    });
    if (fnError) {
      setSubmitting(false);
      setReviewError('Failed to send verification code.');
      return;
    }

    setSubmitting(false);
    setStep('otp');
  };

  // 2️⃣ Verify OTP
  const handleVerifyOtp = async e => {
    e.preventDefault();
    setReviewError('');
    if (!/^\d{6}$/.test(otp)) {
      setReviewError('Enter a 6-digit code.');
      return;
    }
    setSubmitting(true);
    const { data: raw, error } = await supabase.functions.invoke('verifyReviewOtp', {
      body: JSON.stringify({ review_id: reviewId, code: otp }),
    });
    setSubmitting(false);

    if (error) {
      setReviewError(error.message || 'Verification failed.');
      return;
    }
    const result = JSON.parse(raw);
    if (result.verified) {
      setShowReviewModal(false);
      fetchReviews();
    } else {
      setReviewError(result.error || 'Invalid or expired code.');
    }
  };

  return (
    <div className="px-4 lg:px-16 py-12 bg-[#FEF8F2] font-sans">
      {/* Header */}
      <div className="sticky top-0 bg-[#FEF8F2] z-10 py-4">
        <Link to="/vendors" className="flex items-center text-[#f97316] font-medium">
          <ArrowLeftIcon className="w-5 h-5 mr-1" /> Back
        </Link>
      </div>

      <div className="relative max-w-3xl mx-auto bg-white p-8 rounded-lg shadow">
        {/* Quote & Review Buttons */}
<div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
  <button
    onClick={() => (window.location = `mailto:${vendor.email}`)}
    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center gap-1"
  >
    <EnvelopeIcon className="w-5 h-5" /> Quote
  </button>
  <button
    onClick={openReview}
    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center gap-1"
  >
    <PencilIcon className="w-5 h-5" /> Write a Review
  </button>
</div>
        {/* Profile & Rating */}
        <div className="flex flex-col items-center space-y-4">
          <img src={imgSrc} alt={vendor.business_name} className="w-32 h-32 rounded-full object-cover" />
          <h1 className="text-3xl font-bold text-[#4A2C24]">{vendor.business_name}</h1>
          {reviews.length > 0 && (
            <div className="flex items-center space-x-1">
              {[1,2,3,4,5].map(i =>
                avgRating >= i ? (
                  <StarSolid key={i} className="w-5 h-5 text-orange-500" />
                ) : (
                  <StarOutline key={i} className="w-5 h-5 text-gray-300" />
                )
              )}
              <span className="ml-2 text-gray-600">{avgRating.toFixed(1)} ({reviews.length})</span>
            </div>
          )}
          <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-lg">
            {vendor.service_categories}
          </span>
          <p className="flex items-center text-[#555]">
            <MapPinIcon className="w-5 h-5 mr-1 text-orange-500" /> {vendor.service_providing_locations}
          </p>
        </div>

        {/* About */}
        <section className="mt-8">
          <h2 className="text-2xl font-semibold text-[#f97316] mb-2">About</h2>
          <p className={`text-[#333] leading-relaxed overflow-hidden ${!showFullAbout && 'line-clamp-4'}`}>
            {vendor.about_us || 'No description provided.'}
          </p>
          {vendor.about_us && (
            <button
              onClick={() => setShowFullAbout(!showFullAbout)}
              className="mt-2 text-orange-600 font-medium hover:underline"
            >
              {showFullAbout ? 'Show Less' : 'Read More'}
            </button>
          )}
        </section>

        {/* Services & Contact */}
        <div className="mt-8 lg:flex gap-8">
          <div className="lg:w-1/2 space-y-8 pr-8 border-r-2 border-dotted border-[#fed7aa]">
            <div>
              <h3 className="text-xl font-semibold text-[#f97316] mb-2">Services Offered</h3>
              <ul className="space-y-1">
                {services.length ? (
                  services.map((s,i) => (
                    <li key={i} className="flex items-center text-[#444]">
                      <CheckIcon className="w-5 h-5 mr-2 text-[#f97316]" />{s}
                    </li>
                  ))
                ) : (
                  <li className="italic text-gray-600">No services listed.</li>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-[#f97316] mb-2">Contact Information</h3>
<p className="flex items-center mb-1">
  <EnvelopeIcon className="w-5 h-5 mr-2 text-[#f97316]" /> {vendor.email}
</p>

<p className="flex items-center mb-1">
  <PhoneIcon className="w-5 h-5 mr-2 text-[#f97316]" />
  <span className="font-medium mr-1">Mobile:</span>
  {maskPhone(vendor.phone_number)}
</p>

<p className="flex items-center mb-1">
  <img
    src="/assets/whatsapp.png"
    alt="WhatsApp"
    className="w-5 h-5 mr-2"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  />
  <span className="font-medium mr-1">WhatsApp:</span>
  {maskPhone(vendor.whatsapp_number)}
</p>

<div className="mt-3 flex justify-center">
  <button
    className="px-4 py-2 rounded bg-orange-600 text-white font-semibold hover:bg-orange-700 transition"
    onClick={() => setShowContactModal(true)}
  >
    Show Contact Info
  </button>
</div>				
</div>

            {avail && (
              <div>
                <h3 className="text-xl font-semibold text-[#f97316] mb-2">Availability</h3>
                <p className="italic text-[#555]">{avail}</p>
              </div>
            )}
          </div>

          {/* Latest Three Reviews */}
          <div className="lg:w-1/2 mt-8 lg:mt-0 lg:pl-8">
            <h3 className="text-xl font-semibold text-[#f97316] mb-4">Reviews</h3>
            <div className="space-y-4">
              {topReviews.length ? (
                topReviews.map(r => (
                  <div key={r.id} className="p-4 border rounded">
                    <div className="flex items-center mb-2">
                      {[1,2,3,4,5].map(i =>
                        r.rating >= i ? (
                          <StarSolid key={i} className="w-5 h-5 text-orange-500" />
                        ) : (
                          <StarOutline key={i} className="w-5 h-5 text-gray-300" />
                        )
                      )}
                    </div>
                    <p className="italic text-[#333]">“{r.review_text}”</p>
                    <p className="font-medium text-[#555] mt-1">— {r.reviewer_name}</p>
                  </div>
                ))
              ) : (
                <p className="italic text-gray-600">No reviews yet.</p>
              )}
            </div>
            {reviews.length > 3 && (
              <button
                onClick={() => setShowAllReviewsModal(true)}
                className="mt-4 text-orange-600 font-medium hover:underline"
              >
                Show All Reviews
              </button>
            )}
          </div>
        </div>
      </div>

      {/* All Reviews Modal */}
      {showAllReviewsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg relative">
            <button
              onClick={() => setShowAllReviewsModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-bold mb-4">All Reviews</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {reviews.map(r => (
                <div key={r.id} className="p-4 border rounded">
                  <div className="flex items-center mb-2">
                    {[1,2,3,4,5].map(i =>
                      r.rating >= i ? (
                        <StarSolid key={i} className="w-5 h-5 text-orange-500" />
                      ) : (
                        <StarOutline key={i} className="w-5 h-5 text-gray-300" />
                      )
                    )}
                  </div>
                  <p className="italic text-[#333]">“{r.review_text}”</p>
                  <p className="font-medium text-[#555] mt-1">— {r.reviewer_name}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 text-right">
              <button
                onClick={() => setShowAllReviewsModal(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal (OTP flow) */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            {step === 'input' ? (
              <>
                <h2 className="text-xl font-bold mb-4">Write a Review</h2>
                {reviewError && <p className="text-red-600 mb-2">{reviewError}</p>}
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  {/* Rating */}
                  <div>
                    <label className="block font-medium">Rating</label>
                    <div className="flex gap-2 mt-1">
                      {[1,2,3,4,5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                        >
                          {reviewForm.rating >= n ? (
                            <StarSolid className="w-6 h-6 text-orange-500" />
                          ) : (
                            <StarOutline className="w-6 h-6 text-gray-300" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Name */}
                  <div>
                    <label className="block font-medium">Name</label>
                    <input
                      name="reviewer_name"
                      value={reviewForm.reviewer_name}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block font-medium">Email</label>
                    <input
                      type="email"
                      name="reviewer_email"
                      value={reviewForm.reviewer_email}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  {/* Review Text */}
                  <div>
                    <label className="block font-medium">Review</label>
                    <textarea
                      name="review_text"
                      rows={4}
                      value={reviewForm.review_text}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowReviewModal(false)}
                      className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                    >
                      {submitting ? 'Sending OTP…' : 'Submit & Verify Email'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-4">Enter Verification Code</h2>
                {reviewError && <p className="text-red-600 mb-2">{reviewError}</p>}
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <input
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    placeholder="123456"
                    className="w-full border p-2 rounded"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowReviewModal(false)}
                      className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting ? 'Verifying…' : 'Verify & Publish Review'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    
{/* Divider + Portfolio Section */}
<div className="border-t-2 border-dotted border-orange-400 my-6"></div>
<div className="bg-white max-w-3xl mx-auto px-8 pb-10 text-center">
  <h3 className="text-xl font-bold text-[#f97316] mb-4">Portfolio Details</h3>
  
  <div className="flex justify-center gap-6 flex-wrap items-center">
    {vendor.website_url && (
      <a href={vendor.website_url} target="_blank" rel="noopener noreferrer" title="Website">
        <img src="/assets/website.png" alt="Website" className="w-8 h-8 hover:scale-110 transition-transform" />
      </a>
    )}
    {vendor.portfolio_link && (
      <a href={vendor.portfolio_link} target="_blank" rel="noopener noreferrer" title="Portfolio">
        <img src="/assets/portfolio.png" alt="Portfolio" className="w-8 h-8 hover:scale-110 transition-transform" />
      </a>
    )}
    {vendor.facebook_link && (
      <a href={vendor.facebook_link} target="_blank" rel="noopener noreferrer" title="Facebook">
        <img src="/assets/fb.png" alt="Facebook" className="w-8 h-8 hover:scale-110 transition-transform" />
      </a>
    )}
    {vendor.instagram_link && (
      <a href={vendor.instagram_link} target="_blank" rel="noopener noreferrer" title="Instagram">
        <img src="/assets/insta.png" alt="Instagram" className="w-8 h-8 hover:scale-110 transition-transform" />
      </a>
    )}
  </div>
</div>

{/* Contact Info Request Modal */}
{showContactModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg p-6 max-w-sm w-full relative">
      <button
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
        onClick={() => setShowContactModal(false)}
      >
        <XMarkIcon className="w-5 h-5" />
      </button>
      <h2 className="text-xl font-bold mb-4">Request Vendor Contact Info</h2>
      {contactSuccess ? (
        <div className="text-green-600 font-semibold text-center py-6">
          Check your email for the vendor's phone and WhatsApp number!
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setContactSubmitting(true);
            setContactError('');
            // 1. Insert into contact_requests
            const { data, error } = await supabase
              .from('contact_requests')
              .insert({
                vendor_id: vendor.id,
                requester_name: contactName,
                requester_email: contactEmail,
                email_sent: false
              })
              .select()
              .single();
            if (error) {
              setContactError('Error submitting request. Please try again.');
              setContactSubmitting(false);
              return;
            }
            // 2. Call Edge Function using supabase.functions.invoke
            try {
              const { data: raw, error: funcError } = await supabase.functions.invoke('sendContactInfo', {
                body: JSON.stringify({ request_id: data.id }),
              });
              if (funcError) {
                setContactError('Failed to send email. Please try again.');
              } else {
                setContactSuccess(true);
              }
            } catch (err) {
              setContactError('Failed to send email. Please try again.');
            }
            setContactSubmitting(false);
          }}
        >
          <div>
            <label className="block font-medium">Name</label>
            <input
              type="text"
              required
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block font-medium">Email</label>
            <input
              type="email"
              required
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={contactConsent}
              onChange={e => setContactConsent(e.target.checked)}
              className="mr-2"
              required
            />
            <span className="text-sm">
              I consent to my email being stored and used to receive vendor contact info.
            </span>
          </div>
          {contactError && (
            <div className="text-red-600 font-medium">{contactError}</div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setShowContactModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              disabled={contactSubmitting || !contactName || !contactEmail || !contactConsent}
            >
              {contactSubmitting ? 'Submitting…' : 'Request Info'}
            </button>
          </div>
        </form>
      )}
    </div>
  </div>
)}
</div>
  );
}